import React, { useState } from 'react'

import styled from 'styled-components'

import DownloadSystemReport from './DownloadSystemReport'
import EmailSystemReport from './EmailSystemReport'

import Toast, { ToastType } from 'components/Toast'
import Modal from 'components/Modal'

import GA4 from 'helpers/ga4'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import { DeviceBase } from 'types/Device'

import { Customer } from 'types/Customer'
import Api from 'services/api'
import { SystemReportData } from 'types/SystemReportData'

import blobToFileDownload from 'helpers/blobToFileDownload'

type Props = {
  closeCallback: () => void
  isDisplayed: boolean
  devices: Array<DeviceBase>
  customer: Customer
}

export type EmailToastType = {
  open: boolean
  title: string
  type: ToastType
  message: string
}

export type FormData = {
  selectedDeviceIds: Array<string>,
  useServiceContractStatus: boolean,
  useSaveReportStatus: boolean,
  useImageOnFile: boolean,
  notes: string,
  imageId: string,
  firstName: string,
  lastName: string
}

export const REPORT_TYPES = {
  'DOWNLOAD_SYSTEM_REPORT': 'System Report',
  'EMAIL_SYSTEM_REPORT': 'Email'
}

const SystemReportsModal = ({ closeCallback, isDisplayed, devices, customer }: Props) => {
  const [formData, setFormData] = useState<FormData>({
    selectedDeviceIds: [],
    useServiceContractStatus: true,
    useSaveReportStatus: true,
    useImageOnFile: true,
    notes: '',
    imageId: '',
    firstName: customer?.firstName ?? '',
    lastName: customer?.lastName ?? ''
  })

  const [reportType, setReportType] = useState<string>(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
  const [errors, setErrors] = useState<{ devices?: string }>({})
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [emailToast, setEmailToast] = useState<EmailToastType>({
    open: false,
    title: '',
    type: 'success',
    message: ''
  })

  const handleEmailToastClose = () => {
    setEmailToast({
      open: false,
      title: '',
      type: 'success',
      message: ''
    })
  }

  const handleFormData = (fieldName, fieldValue) => {
    setFormData({
      ...formData,
      [fieldName]: fieldValue
    })
  }

  const resetAndCloseModal = () => {
    setReportType(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
    setErrors({})
    closeCallback()
  }

  const generateReportPayload = (emailNotes: string): SystemReportData => {
    const { selectedDeviceIds, useImageOnFile, imageId, firstName, lastName, notes, useServiceContractStatus, useSaveReportStatus } = formData

    if (devices.length === 1) {
      handleFormData('selectedDeviceIds', [devices[0].deviceId])
    }
    const selectedIds =
      devices.length === 1 ? [devices[0].deviceId] : selectedDeviceIds

    return {
      customerId: customer?.id,
      useServiceContractStatus,
      useSaveReportStatus,
      notes: reportType === REPORT_TYPES.EMAIL_SYSTEM_REPORT ? emailNotes : notes,
      reportGeneratedBy: firstName + ' ' + lastName,
      useImageOnFile,
      imageId: useImageOnFile ? imageId : '',
      selectedDeviceIds: selectedIds.join(',')
    }
  }

  const validateForm = () => {
    if (devices.length > 1 && formData.selectedDeviceIds.length === 0) {
      setErrors(oldErrors => ({ ...oldErrors, devices: 'Select at least one system' }))
      return true
    }
  }

  const selectEmailSystemReport = () => {
    const hasFormError = validateForm()
    if (hasFormError) return
    setReportType(REPORT_TYPES.EMAIL_SYSTEM_REPORT)
  }

  const handleGenerateReport = () => {
    const { useServiceContractStatus, useImageOnFile, useSaveReportStatus, notes } = formData
    const hasFormError = validateForm()
    if (hasFormError) return
    const payload = generateReportPayload('')
    Api.SystemReports.getReport(payload).then((response) => {
      blobToFileDownload(response, 'SystemReport.pdf')
    })

    const label = [
      useImageOnFile ? 'Logo Included' : '',
      useServiceContractStatus ? 'Contract Checked' : '',
      useSaveReportStatus ? 'Report Saved' : '',
      notes ? 'Notes Included' : ''
    ].filter(Boolean).join(', ')

    GA4.sendEvent({
      category: 'Customer Detail Page',
      action: 'System Report PDF Generated',
      label
    })
    closeCallback()
  }

  const sendEmail = async (notes: string) => {
    try {
      if (devices.length > 1 && formData.selectedDeviceIds.length === 0) {
        setErrors(oldErrors => ({ ...oldErrors, devices: 'Select at least one system' }))
        return
      }
      setIsEmailSent(true)
      const payload = generateReportPayload(notes)
      await Api.SystemReports.sendEmail(payload)
      setEmailToast({
        open: true,
        title: 'Success!',
        type: 'success',
        message: 'Your email has been sent.'
      })
    } catch {
      setEmailToast({
        open: true,
        title: 'Error',
        type: 'error',
        message: 'Your email has not been sent.'
      })
    } finally {
      setIsEmailSent(false)
      resetAndCloseModal()
      setReportType(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
    }
  }

  return (
    <>
      <S.Modal
        title={reportType}
        variant="sm"
        closeCallback={resetAndCloseModal}
        showCloseButton={reportType === 'System Report'}
        {...{ isDisplayed }}
      >
        {
          reportType === REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT
            ? <DownloadSystemReport
              handleFormData={handleFormData}
              handleGenerateReport={handleGenerateReport}
              selectEmailSystemReport={selectEmailSystemReport}
              errors={errors}
              resetAndCloseModal={resetAndCloseModal}
              devices={devices}
              formData={formData}
              customerId={customer?.id}
              customerEmail={customer.email ?? ''}
              />
            : <EmailSystemReport
              sendEmail={sendEmail}
              resetAndCloseModal={resetAndCloseModal}
              customerName={formData.firstName + ' ' + formData.lastName}
              customerEmail={customer.email ?? ''}
              isEmailSent={isEmailSent}
              />
        }
      </S.Modal>
      <Toast
        title={emailToast.title}
        type={emailToast.type}
        message={emailToast.message}
        onClose={handleEmailToastClose}
        open={emailToast.open}
        autoHideDuration={6000}
      />
    </>
  )
}

export default SystemReportsModal

const S = {
  Modal: styled(Modal)`
    color: ${color('secondaryText')};
    @media ${screenSize.small} {
      padding-bottom: 70px;
    }
  `
}
